<template>
  <tile class="opc-zobundle">
    <div class="plan-card__badge" v-if="currentPlanTrialDays">
      <span class="plan-card__badge-text">{{ $t('freetrial.duration_desc', [currentPlanTrialDays]) }}</span>
    </div>
    <foldable v-model="isExpanded" :title="title">
      <template #head>
        <CardHeader :logo="logo" :title="title">
          <template #head-right-content>
            <div class="opc-zm-base-card__view-detail-button">
              <zm-button
                v-if="showPlanDetails"
                data-testid="checkout-button-164"
                class="opc-zm-base-card__link"
                type="link"
                @click="handlePlanDetailsClick"
              >
                {{ planDetailLabel }}
              </zm-button>
            </div>
          </template>
          <div
            class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset"
            v-if="!isExpanded"
            v-html="plansSummary"
          ></div>
        </CardHeader>
      </template>
      <plan-highlights
        v-bind="planHighlightsProps"
      />
      <div class="opc-zobundle__main">
        <div class="opc-plan__quantity-wrapper">
          <h3 class="opc-plan__quantity-title">{{ $t('billing.zone.select_license') }}</h3>
          <p class="opc-plan__quantity-tip">{{ $t('bizPlus.select_license_tip_desc') }}</p>
          <InputNumberWithPopover
            class="opc-plan__quantity-selector"
            v-model="form.selectedNumber"
            size="large"
            controls-position="both"
            :min="minHost"
            :max="maxHost"
            :increase-tips="increaseTips"
            :data-testid="`checkout-input-license-${selectedPlan ? selectedPlan.plan_code : ''}`"
            @change="changeLicense"
            :label="title + ' Select the number of users'"
            :aria-label="title + ' Select the number of users'"
          />
        </div>
        <div class="opc-ft-cycle__wrapper">
          <h3 class="opc-ft-cycle__title">{{ $t('billing.simplify.subscription_cycle') }}</h3>
          <cycle-radio-group
            v-model="form.billingCycle"
            :plans="billingCycleList"
            :action="switchCycleAction"
            aria-label="zoom one business plus free trial billing cycle"
            class="opc-card-radio"
          >
            <zm-radio
              v-for="plan in billingCycleList"
              :key="plan.billing_cycle"
              :data-testid="`checkout-radio-billing-cycle-${plan.plan_code}`"
              :label="plan.billing_cycle"
              class="opc-ft-cycle__radio"
            >
              <div class="opc-ft-cycle__item">
                <div class="opc-ft-cycle__header">
                  <div class="opc-ft-cycle__label">{{ plan.billingCycleName }}</div>
                  <div class="opc-ft-cycle__free">{{ $t('freetrial.duration_desc', [plan.trialDays]) }}</div>
                </div>
                <div class="opc-ft-cycle__price-wrap">
                  <template v-if="plan.discountedAmount">
                    <div class="opc-ft-cycle__price-now" :aria-label="`discounted price ${$money(plan.discountedAmount)}`">
                      {{ $money(plan.discountedAmount) }}
                    </div>
                  <div class="opc-ft-cycle__price-old" :aria-label="`regular price ${$money(plan.amount)}`">{{ $money(plan.amount) }}</div>
                  <div class="opc-ft-cycle__badge" v-if="plan.isPercentageDiscount">
                    {{ plan.discount }}%
                    <span style="text-transform: uppercase">{{ $t('billing.coupon.discount') }}</span>
                  </div>
                  </template>
                  <template v-else>
                    <div class="opc-ft-cycle__price-original">
                      <span class="opc-ft-cycle__price-original-amount">{{ $money(plan.amount) }}</span>
                      <span class="opc-ft-cycle__price-original-unit">{{ getPlanUnitWithCycle(plan.billing_cycle) }}</span>
                    </div>
                  </template>
                </div>
                <template v-if="plan.discountedAmount">
                  <div
                    class="opc-ft-cycle__price-desc"
                    v-html="$t('bizPlus.priceDesc', { amount: $money(plan.amount) })"
                  ></div>
                </template>
              </div>
            </zm-radio>
          </cycle-radio-group>
        </div>
        <div class="opc-pbx-fees__wrapper">
          <div class="opc-pbx-fees__title">
            <span id="pbx-fees-title">{{ $t('billing.pbx.calling_fee_label.metered') }}</span>
            <zm-popover
              placement="top"
              trigger="hover"
              :width="320">
              <template #content>
                <span v-html="pbxFeesTooltipContent"></span>
              </template>
              <tips-icon
                type="info"
                :aria-label="`Learn more about ${$t('billing.pbx.calling_fee_label.metered')}`"
              />
            </zm-popover>
          </div>
          <zm-radio-group
            class="opc-card-radio opc-pbx-fees__radios"
            aria-labelledby="pbx-fees-title"
            v-model="form.pbxBaseFeeCode"
            @input="handlePbxBaseFeeChange"
            :aria-label="$t('billing.pbx.calling_fee_label.metered')"
          >
            <zm-radio
              v-for="item in pbxFeesOptions"
              :data-testid="`checkout-radio-billing-cycle-${item.value}`"
              :key="item.value"
              :label="item.value"
            >
              <p class="opc-pbx-fees__title">{{ item.title }}</p>
              <p class="opc-pbx-fees__subtitle">
                <span class="opc-pbx-fees__subtitle-price">{{ item.subTitle }}</span>
                <span class="opc-pbx-fees__subtitle-desc">{{ item.description }}</span>
                <priceVATTip />
              </p>
            </zm-radio>
          </zm-radio-group>
        </div>
      </div>
    </foldable>
  </tile>
</template>

<script lang="jsx">
import _ from 'lodash';
import { computedPbxBasePlans } from '@/common/service/computers/plans/pbx';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import CardHeader from '@/components/plan-card-header.vue';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import PlanDetails from '@/plans/ZOOM_MEETING/dialog/plan-details';
import { BillingCycleEnum } from '@/common/constants';
import { plansof } from '@/utils/chain';
import { planTypes } from '@/common/constants';
import { mapGetters } from 'vuex';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { mapMutations, mapActions } from 'vuex';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';
import phoneIcon from '@/assets/plans/bizplus/checkmark.svg';
import { couponHelper } from '@/views/config/coupon/helper/get';
import { i18n_cycle } from '@/utils/i18n-shortcuts';
import { $desc } from '@/components/service/config-desc';
import TipsIcon from '@/components/tips-icon/index.vue';
import { getPlanLogo } from '@/common/plan-logo-map';
import { getFreeTrialPlanCollapseText } from '@/common/product';
import { getPlanUnitWithCycle } from '@/common/product-unit';
import { getOnlinePlanName } from '@/common/product';

// Local component for plan highlights
const PlanHighlights = {
  name: 'PlanHighlights',
  props: {
    benefits: {
      type: Array,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    isExpanded: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    return () => (
      <div class="opc-plan-highlights" style={{ display: props.isExpanded ? 'block' : 'none' }}>
        <ul class="opc-plan-highlights__list">
          {props.benefits.map((benefit, index) => (
            <li key={index} class="opc-plan-highlights__item">
              <span class="opc-plan-highlights__icon" aria-hidden="true">
                <img src={props.icon} alt="" />
              </span>
              <span class="opc-plan-highlights__text" innerHTML={benefit.text} />
            </li>
          ))}
        </ul>
      </div>
    );
  },
};

export default {
  name: 'ConfigBaseFt',
  components: {
    Foldable,
    Tile,
    CardHeader,
    InputNumberWithPopover,
    PlanHighlights,
    TipsIcon,
  },
  data() {
    return {
      isExpanded: true,
      logo: getPlanLogo(planTypes.ZOOM_ONE_BUNDLE),
      title: this.$t('billing.trial.title', [this.$t('billing.planform.title.zoom_one_biz_plus')]),
      planDetailsDialog: new ServiceConfirm(PlanDetails, this.$store),
      planType: planTypes.ZOOM_ONE_BUNDLE,
      form: {
        selectedNumber: 1,
        billingCycle: BillingCycleEnum.ANNUAL,
        pbxBaseFeeCode: '',
      },
      phoneIcon,
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getTypeGroupSelectedPlans', 'getSelectedPlans', 'couponDiscountPlanMap']),
    currentPlanTrialDays() {
      return this.selectedPlan?.trialDays;
    },
    pbxBasePlans() {
      return computedPbxBasePlans(this.planMapFlat);
    },
    selectedPbxBasePlan() {
      return computedPbxBasePlans(this.getSelectedPlans)[0];
    },
    pbxBasePlanCycleMap() {
      return _.groupBy(this.pbxBasePlans, 'billing_cycle');
    },
    pbxFeesOptions() {
      const { payAsYouGo = [] } = this.pbxBasePlanCycleMap;

      if (!payAsYouGo || payAsYouGo.length === 0) {
        return [];
      }

      return payAsYouGo.map((plan) => {
        const callingFeesText = getZPCallingFeesText(plan);
        return {
          value: plan.plan_code,
          title: callingFeesText?.title || '',
          subTitle: this.$money(plan.amount, { forceDiff: true }),
          description: callingFeesText?.unit || ''
        };
      });
    },
    plansSummary() {
      const callingPlanLabels = getFreeTrialPlanCollapseText({
        amount: this.selectedPlan?.amount || 0,
        billingCycle: this.form.billingCycle,
        quantity: this.form.selectedNumber,
        cycleValue: this.selectedPlan?.cycleValue,
        unit: this.selectedPlan?.unit,
      });

      const usageFeeOption = this.pbxFeesOptions?.[0];

      if (!usageFeeOption) {
        return callingPlanLabels;
      }
      const usageFeeLabel = `${usageFeeOption.title}  •  ${this.$t('billing.pbx.calling_fee_name.pay_you_go.MC')}  •  ${
        usageFeeOption.subTitle
      }${usageFeeOption.description}`;
      return [callingPlanLabels, usageFeeLabel].join('<br/>');
    },
    selectedPlan() {
      return this.getTypeGroupSelectedPlans[this.planType]?.[0];
    },
    showPlanDetails() {
      return this.isExpanded && this.planDetailLabel;
    },
    planDetailLabel() {
      return this.$t('billing.plan_details');
    },
    billingCycleList() {
      const planList = plansof(this.planMapFlat)
        .type(this.planType)
        .filter((plan) => plan.isOnlineFreeTrial)
        .get();

      return _.map(planList, (plan) => {
        const { singlePlanDiscountAmount, discountType, discount } = this.couponDiscountPlanMap[plan.plan_code] ?? {};

        return {
          ...plan,
          discountedAmount: plan?.amount - singlePlanDiscountAmount,
          discountAmount: singlePlanDiscountAmount,
          isPercentageDiscount: couponHelper.isPercentageDiscount(discountType),
          discount: discount,
          billingCycleName: this.i18n_cycle(plan.billing_cycle),
        };
      });
    },
    minHost() {
      return 1;
    },
    maxHost() {
      return this.selectedPlan?.trialMaxQuantity || 1;
    },
    increaseTips() {
      if (this.form.selectedNumber >= this.maxHost) {
        return this.$t('billing.zcm.max_license', [this.maxHost]);
      }
      return '';
    },
    trialBenefits() {
      return [
        {
          text: this.$t('bizPlus.features.item1'),
        },
        {
          text: this.$t('bizPlus.features.item2'),
        },
        {
          text: this.$t('bizPlus.features.item3'),
        },
        {
          text: this.$t('bizPlus.features.item4'),
        },
        {
          text: this.$t('bizPlus.features.item5'),
        },
        {
          text: this.$t('bizPlus.features.item6'),
        },
      ];
    },
    planHighlightsProps() {
      return {
        benefits: this.trialBenefits,
        icon: this.phoneIcon,
        isExpanded: this.isExpanded,
      };
    },
    pbxFeesTooltipContent() {
      // todo by arvin
      return `<a href="/billing/pbx/rates" target="_blank">View rates and coverage</a> for  receiving and making calls outside of US & Canada.`
    },
    trackingPlanKey() {
      return `${getOnlinePlanName(this.selectedPlan)} free trial`;
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),
    i18n_cycle,
    getPlanUnitWithCycle,
    updateForm({ selectedNumber, billingCycle, pbxBaseFeeCode }) {
      this.form.selectedNumber = selectedNumber;
      this.form.billingCycle = billingCycle;
      this.form.pbxBaseFeeCode = pbxBaseFeeCode;
    },
    async handlePlanDetailsClick() {
      this.$tracking.buyInteraction('plan details selected', this.title);
      await this.planDetailsDialog.confirm({
        planCategory: 'bizplus',
        hiddenExploreOtherPlans: true,
      });
    },
    changeLicense(val) {
      this.form.selectedNumber = val;
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: val },
      });
    },
    async switchCycleAction(oldPlan, newPlan) {
      // when the current license less than max, get current license, otherwise get maxHost
      const quantity = this.form.selectedNumber <= this.maxHost ? this.form.selectedNumber : this.maxHost;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      await this.switchBillingCycleAction([oldPlan?.plan_code, newPlanInfo]);

      this.afterSwitchCycleTracking();
    },
    afterSwitchCycleTracking() {
      // TODO: add event tracking
    },
    handlePbxBaseFeeChange(val) {
      this.form.pbxBaseFeeCode = val;
      this.updateSelectPlan({
        plan_code: val,
        cartInfo: { selected_number: 1 },
      });
    },
    descRender() {
      return <p innerHTML={this.$t('bizPlus.pbx_fees_tips')}></p>;
    },
  },
  mounted() {
    $desc(this.descRender());
  },
  created() {
    const selectedPlan = this.selectedPlan;

    if (selectedPlan) {
      this.updateForm({
        selectedNumber: selectedPlan.cartInfo?.selected_number,
        billingCycle: selectedPlan.billing_cycle,
        pbxBaseFeeCode: this.selectedPbxBasePlan?.plan_code,
      });
    }
  },
  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction(
          `product details accordion`,
          `${this.trackingPlanKey} - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
    "form.selectedNumber": {
      handler(val, oldVal) {
        const newIsMore = val > oldVal;
        this.$tracking.buyInteraction(
          'number of licenses',
          `${this.trackingPlanKey} - ${newIsMore ? 'increase' : 'decrease'} - ${val}`,
        );
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-zobundle {
  .opc-zm-base-card {
    &__head-right {
      width: 100%;
    }
  }

  // @include media(mobile) {
  //   .opc-foldable {
  //     &__action {
  //       top: 16px !important;
  //     }
  //   }
  // }

  &__main {
    .opc-plan {
      &__divider {
        height: 1px;
        background: var(--zoom-color-border-subtle-neutral, #DFE3E8);
        width: 100%;
        margin: 16px 0px;
      }
    }
  }

  .plan-card {
    &__badge {
      align-self: flex-start;
      border-radius: 100px;
      padding: 0 4px;
      height: 16px;
      display: inline-flex;
      align-items: center;
      background: linear-gradient(97deg, #084845 10.36%, #09a639 89.64%);
      margin-bottom: 16px;
    }
    &__badge-text {
      color: #fff;
      font-size: 12px;
      line-height: 16px;
      text-transform: uppercase;
    }
  }

  .opc-card-radio {
    .zbo-radio {
      width: 100%;

      &__wrap,
      &__label--inner {
        width: 100%;
      }
    }
  }
}

.opc-plan-highlights {
  padding-top: 8px;

  @include media(desktop, tablet) {
    padding-left: 56px;
  }

  @include media(mobile) {
    padding-top: 16px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    list-style: none;
    gap: 4px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__icon {
    display: inline-flex;
  }

  &__text {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 128.571% */
    letter-spacing: -0.15px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

    strong {
      font-weight: 600;
    }
  }
}

.opc-plan {
  &__quantity-wrapper {
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
  }
  &__quantity-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }
  &__quantity-tip {
    color: var(--zoom-color-text-neutral, #686F79);
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.15px;
    margin: 4px 0px 8px;
  }
}

.opc-ft-cycle {
  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }

  &__card {
    border: 2px solid #0d6ede;
    background: #ebf4ff;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    cursor: pointer;
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    margin-bottom: 2px;
  }

  &__label {
    font-size: 16px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    line-height: 20px;
    font-weight: 600;
  }

  &__free {
    font-size: 16px;
    color: var(--zoom-color-text-success, #247F40);
    line-height: 20px; /* 125% */
    letter-spacing: var(--font-font-letterSpacing-16, -0.31px);
  }

  &__price-wrap {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__price-now {
    font-size: 24px;
    font-weight: 600;
    color: var(--zoom-color-text-success, #247F40);
    letter-spacing: 0.07px;
    line-height: 28px;
  }

  &__price-old {
    font-size: 24px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    text-decoration: line-through;
    line-height: 28px;
  }

  &__price-original {
    &-amount {
      font-size: 24px;
      font-weight: 600;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      line-height: 32px;
    }
    &-unit {
      font-size: 14px;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }
  }

  &__badge {
    height: 16px;
    line-height: 16px;
    padding: 0 8px;
    border-radius: 100px;
    font-size: 12px;
    color: #FFFFFF;
    background: linear-gradient(97deg, #084845 10.36%, #09a639 89.64%);
  }

  &__price-desc {
    font-size: 14px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }
}

.opc-pbx-fees {
  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }

  &__subtitle {
    &-price {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-size: 24px;
      font-weight: 590;
      line-height: 28px; /* 116.667% */
      letter-spacing: 0.07px;
    }

    &-desc {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-size: 14px;
      font-weight: 400;
      line-height: 18px; /* 128.571% */
      letter-spacing: -0.15px;
    }
  }

  &__tooltip-btn {
    &.zbo-button {
      padding: 0px;
      min-width: auto;
      margin-left: 4px;
    }
  }
}
</style>
